import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Get user and integrations from /profile endpoint
          const userData = await authService.getUserAndPractices();
          setCurrentUser(userData.user);
          setIntegrations(userData.integrations || []);
        } catch (err) {
          console.error('Failed to get current user:', err);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.login(email, password);
      if (result?.token) {
        setToken(result.token);
      }
      // After login, get user and integrations from /profile
      const userData = await authService.getUserAndPractices();
      setCurrentUser(userData.user);
      setIntegrations(userData.integrations || []);

      return userData.user;
    } catch (err) {
      console.error('Login failed:', err);

      // Extract the actual error message from the backend response
      let errorMessage = 'Failed to login';

      if (err.response && err.response.data) {
        // Handle authentication errors (401) - backend returned error
        if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        // Network/connection error - backend is down
        errorMessage = 'Unable to connect to server. Please try again later.';
      } else if (err.message) {
        // Other errors - show generic message
        errorMessage = 'Unable to connect to server. Please try again later.';
      }

      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Call API first (while token still in localStorage for HIPAA audit logging)
    await authService.logout();

    // Then cleanup local state and storage
    localStorage.removeItem('auth_token');
    setToken(null);
    setCurrentUser(null);
    setError(null);
    window.location.href = '/login';
  };

  const refreshToken = async () => {
    try {
      const { token } = await authService.refreshToken();
      localStorage.setItem('auth_token', token);
      setToken(token);
      return token;
    } catch (err) {
      console.error('Failed to refresh token:', err);
      logout();
      throw err;
    }
  };

  const value = {
    currentUser,
    integrations,
    token,
    loading,
    error,
    isAuthenticated: !!token && !!currentUser,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
